import {
  BooleanField,
  BooleanInput,
  BulkDeleteButton,
  ChipField,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  FunctionField,
  List,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import { CrudPermissions } from "../../auth/crud-permissions";
import {
  ConversionInputStreamSource,
  ConversionInputStreamSourceList,
  getSensitiveFieldsForSource,
  MASKED_VALUE,
} from "@arrowsup/platform-dtos";
import InputIcon from "@mui/icons-material/Input";
import { Box, Divider, Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import { omit } from "lodash";
import { ReactElement } from "react";
import {
  formatTimestamp,
  parseTimestamp,
} from "../../../logic/api/timestamp-codec";
import { TopHelpInfoText } from "../../help/top-help-info-text";

export const ConversionInputStream = (perms: CrudPermissions) => {
  return (
    <Resource
      name="conversion-input-stream"
      key="conversion-input-stream"
      options={{ label: "Input Streams" }}
      list={ConversionInputStreamList(perms)}
      create={perms.create ? ConversionInputStreamCreate() : undefined}
      edit={perms.update ? ConversionInputStreamEdit() : undefined}
      icon={InputIcon}
    />
  );
};

const ConversionInputStreamListInner: React.FC<{
  perms: CrudPermissions;
}> = ({ perms }) => {
  return (
    <>
      <TopHelpInfoText text="Pull conversions from external sources like Dialpad or Twilio call logs." />
      <List>
        <Datagrid
          rowClick={perms.update ? "edit" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="Stream ID" />
          <TextField source="description" label="Description" />
          <ChipField source="source.name" label="Source" />
          <BooleanField source="enabled" />
          <FunctionField
            label="Start Time"
            render={(record: { startTimeMs?: number | null }) =>
              record.startTimeMs
                ? new Date(record.startTimeMs).toLocaleString()
                : "—"
            }
          />
          <FunctionField
            label="Last Ingested"
            render={(record: { lastIngestedEventTimeMs?: number | null }) =>
              record.lastIngestedEventTimeMs
                ? new Date(record.lastIngestedEventTimeMs).toLocaleString()
                : "—"
            }
          />
          <DateField source="createdAt" label="Created" />
          <DateField source="updatedAt" label="Last Updated" />
        </Datagrid>
      </List>
    </>
  );
};

export const ConversionInputStreamList = (perms: CrudPermissions) => {
  return <ConversionInputStreamListInner perms={perms} />;
};

const SourceChooser: React.FC = () => {
  return (
    <SelectInput
      source="source.name"
      // eslint-disable-next-line
      choices={ConversionInputStreamSourceList.map((s) => ({
        // eslint-disable-next-line
        id: s,
        // eslint-disable-next-line
        name: s.charAt(0).toUpperCase() + s.slice(1),
      }))}
      required
      fullWidth
    />
  );
};

const NoSourceSet: React.FC = () => {
  return <Typography>No source set.</Typography>;
};

const DialpadSourceConfig: React.FC = () => {
  return (
    <TextInput
      label="API Key"
      source="source.apiKey"
      type="password"
      fullWidth
      required
      helperText="Company admin API key from Dialpad. Write-only — will show as masked when saved."
    />
  );
};

const SourceConfig: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const sourceName: ConversionInputStreamSource | undefined = useWatch({
    name: "source.name",
  });

  const configMap: Record<ConversionInputStreamSource, ReactElement> = {
    dialpad: <DialpadSourceConfig />,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Divider sx={{ pb: 2 }}>Source Config</Divider>
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        sourceName ? configMap[sourceName] ?? <NoSourceSet /> : <NoSourceSet />
      }
    </Box>
  );
};

const StartTimeInput = () => {
  const record = useRecordContext();
  const isNew = record?.id === undefined;

  return (
    <DateTimeInput
      label="Start Time"
      source="startTimeMs"
      parse={parseTimestamp}
      format={formatTimestamp}
      helperText="Events before this time will not be ingested. Cannot be edited after the stream is created."
      fullWidth
      required
      disabled={!isNew}
    />
  );
};

export const ConversionInputStreamCreate = () => {
  return (
    <Create redirect="list">
      <SimpleForm>
        <TextInput source="description" fullWidth />
        <BooleanInput fullWidth source="enabled" />
        <StartTimeInput />
        <SourceChooser />
        <SourceConfig />
      </SimpleForm>
    </Create>
  );
};

export const ConversionInputStreamEdit = () => {
  return (
    <Edit
      redirect="list"
      transform={(data: Record<string, unknown>) => {
        const cleaned = omit(data, ["createdAt", "updatedAt", "startTimeMs"]);
        const source = cleaned.source as Record<string, unknown> | undefined;

        // If apiKey is the masked value, remove it so the backend preserves
        // the stored value.
        if (source) {
          const sensitiveFields = source?.name
            ? getSensitiveFieldsForSource(source.name as string)
            : [];
          for (const field of sensitiveFields) {
            const value = source[field];
            if (
              value === MASKED_VALUE ||
              (typeof value === "string" && value.trim() === "")
            ) {
              delete source[field];
            }
          }
        }

        return cleaned;
      }}
    >
      <SimpleForm>
        <TextInput source="description" fullWidth />
        <BooleanInput fullWidth source="enabled" />
        <StartTimeInput />
        <SourceChooser />
        <SourceConfig />
      </SimpleForm>
    </Edit>
  );
};
